/**
 * LiabilityDetail — detail view for a liability account (loan, mortgage,
 * or credit card).
 *
 * Sub-tabs:
 *   • loan / mortgage    : Overview · Statements · Payoff
 *   • credit-card        : Overview · Transactions · Statements · Trends · Payoff
 *
 * Phase 84.22.6 — Credit cards used to expose a `Payments` tab that
 * only showed the demo `DEMO_RECENT_PAYMENTS` fixture. That tab is now
 * replaced with a real `Transactions` tab wired to the same
 * `CashTransactionsTab` component used by bank/cash accounts. When a
 * CC statement is imported, its transactions land in `transactions`
 * with `account_id = <card>` — this tab picks them up automatically.
 *
 * Overview KPIs adapt to the sub-type:
 *   • credit-card: shows minimum-payment + credit-utilization %
 *   • loan/mortgage: shows interest-rate + principal-paid %
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import DetailHeader from './DetailHeader';
import SubTabs from './SubTabs';
import CashTrendsTab from './CashTrendsTab';
import CashTransactionsTab from './CashTransactionsTab';
import PaymentActivityList from '@/components/liabilities/PaymentActivityList';
import PaymentStreakBadge from '@/components/liabilities/PaymentStreakBadge';
import MilestoneCelebration from '@/components/liabilities/MilestoneCelebration';
import CloseLiabilityDialog from '@/components/liabilities/CloseLiabilityDialog';
import BalanceProvenanceCard from '@/components/liabilities/BalanceProvenanceCard';
import {
  confirmPaymentEvent, fetchCanonicalBalances, fetchLiabilityPaymentEvents,
  rejectPaymentEvent, reversePaymentEvent,
} from '@/lib/liabilityReconcileApi';
import { formatMoney } from '@/lib/format';
import { ACCOUNT_TYPE_LABEL, ACCOUNTS_TONE } from '@/lib/accountsDemo';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function StubTab({ label }) {
  return (
    <div className="bg-card rounded-xl border border-border px-6 py-12 text-center shadow-sm" data-testid={`accounts-liab-stub-${label.toLowerCase()}`}>
      <p className="text-sm font-medium text-charcoal mb-1">{label}</p>
      <p className="text-xs text-muted-foreground">This section is coming soon.</p>
    </div>
  );
}

export default function LiabilityDetail({ account, onBack, baseCurrency = 'INR', onEdit, onDelete, onSetBalance }) {
  const [tab, setTab] = useState('overview');
  const isCreditCard = account.type === 'credit-card' || account.type === 'credit_card';

  // Phase 85.1 — payment activity + balance provenance. Only rows that live in
  // the `liabilities` table can carry payment events; credit cards stay
  // statement-driven (their balance also moves with purchases and refunds).
  const isRealLiability = account.source_table === 'liabilities';
  const [recon, setRecon] = useState(null);
  const [reconBusy, setReconBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [otherLiabs, setOtherLiabs] = useState([]);

  const loadRecon = useCallback(async () => {
    if (!isRealLiability || !account.id) return;
    let r = await fetchLiabilityPaymentEvents(account.id);
    if (!r?.available) {
      // A transient failure (or a session still restoring) must not silently
      // hide payment activity. Retry once before giving up.
      await new Promise((res) => setTimeout(res, 1200));
      r = await fetchLiabilityPaymentEvents(account.id);
    }
    setRecon(r?.available ? r : null);
  }, [account.id, isRealLiability]);

  useEffect(() => { loadRecon(); }, [loadRecon]);

  const runReconAction = async (fn, event, okMsg) => {
    setReconBusy(true);
    try {
      await fn(event.id);
      toast.success(okMsg);
      await loadRecon();
    } catch {
      toast.error('Could not update that payment.');
    } finally {
      setReconBusy(false);
    }
  };

  const activeEvents = (recon?.events || []).filter(
    (e) => e.match_status === 'user_confirmed' || e.match_status === 'auto_linked',
  );

  const tabs = [
    { id: 'overview',   label: 'Overview' },
    // Phase 84.22.6 — credit cards get a real Transactions tab (was
    // "Payments" backed by demo fixtures). Loans/mortgages have no
    // per-transaction detail to show — their activity is a schedule
    // surfaced by the Payoff tab.
    ...(isCreditCard ? [{ id: 'transactions', label: 'Transactions' }] : []),
    { id: 'statements', label: 'Statements' },
    // Phase 84.19.2 — Trends is credit-card only. On loans/mortgages the
    // "spending by category" pie is meaningless; balance amortises over
    // a schedule which is already surfaced by the Payoff tab.
    ...(isCreditCard ? [{ id: 'trends', label: 'Trends' }] : []),
    { id: 'payoff',     label: 'Payoff' },
  ];

  const outstanding = Math.abs(account.balance || 0);
  // Phase 85.2 — every figure on this page is the ACCOUNT's own money, so it
  // renders in the account's currency. Passing `baseCurrency` here was
  // labelling rupee loans as dirhams without converting anything.
  const ccy = account.currency || baseCurrency;
  const original    = account.original_amount ?? outstanding;
  const paidPct     = original > 0 ? Math.round((1 - outstanding / original) * 100) : 0;
  const utilization  = isCreditCard && account.credit_limit
    ? Math.round((outstanding / account.credit_limit) * 100)
    : null;

  const monthlyPayment = account.next_payment_amount || 0;
  const monthsLeft = monthlyPayment > 0 ? Math.ceil(outstanding / monthlyPayment) : null;

  const overviewMetrics = [
    { label: 'Next payment', value: account.next_payment_amount ? formatMoney(account.next_payment_amount, ccy) : '—', color: '#1E1E1E' },
    { label: 'Due date',     value: account.next_payment_date ? fmtDate(account.next_payment_date) : '—',                color: ACCOUNTS_TONE.warning },
    account.interest_rate
      ? { label: 'Interest rate', value: `${account.interest_rate}% p.a.`, color: '#1E1E1E' }
      : isCreditCard && account.minimum_payment
        ? { label: 'Minimum payment', value: formatMoney(account.minimum_payment, ccy), color: '#1E1E1E' }
        : { label: 'Type', value: ACCOUNT_TYPE_LABEL[account.type] || account.type, color: '#1E1E1E' },
    utilization !== null
      ? { label: 'Credit used',      value: `${utilization}%`, color: utilization > 80 ? ACCOUNTS_TONE.warning : '#1E1E1E' }
      : { label: 'Principal paid',   value: `${paidPct}%`,     color: ACCOUNTS_TONE.positive },
  ];

  return (
    <div data-testid="accounts-liability-detail">
      <DetailHeader
        account={account}
        onBack={onBack}
        balance={formatMoney(outstanding, ccy)}
        balanceLabel={isCreditCard ? 'Current balance' : 'Outstanding balance'}
        balanceColor={ACCOUNTS_TONE.liability}
        onEdit={onEdit}
        onDelete={onDelete}
        onSetBalance={onSetBalance}
      />
      <SubTabs tabs={tabs} active={tab} onChange={setTab} testidPrefix="accounts-liab-tab" />

      {tab === 'overview' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {overviewMetrics.map((m, i) => (
              <div key={i} className="bg-card rounded-xl border border-border px-4 py-4 shadow-sm">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-2">{m.label}</p>
                <p className="num font-display text-lg font-medium" style={{ color: m.color }}>{m.value}</p>
              </div>
            ))}
          </div>

          {/* Repayment progress for loans/mortgages */}
          {!isCreditCard && (
            <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid="accounts-liab-repayment">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-charcoal">Repayment progress</p>
                <p className="text-xs text-muted-foreground">{paidPct}% paid</p>
              </div>
              <div className="h-2 bg-border/60 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${paidPct}%`, backgroundColor: ACCOUNTS_TONE.positive }} />
              </div>
              <div className="flex items-center justify-between mt-2.5">
                <p className="text-[11px] text-muted-foreground">Paid: {formatMoney(original - outstanding, ccy)}</p>
                <p className="text-[11px] text-muted-foreground">Remaining: {formatMoney(outstanding, ccy)}</p>
              </div>
            </div>
          )}

          {/* Credit utilization for cards */}
          {isCreditCard && account.credit_limit && (
            <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid="accounts-liab-utilization">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-charcoal">Credit utilization</p>
                <p className="text-xs text-muted-foreground">{utilization}% used</p>
              </div>
              <div className="h-2 bg-border/60 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${utilization}%`, backgroundColor: (utilization ?? 0) > 80 ? ACCOUNTS_TONE.warning : ACCOUNTS_TONE.positive }}
                />
              </div>
              <div className="flex items-center justify-between mt-2.5">
                <p className="text-[11px] text-muted-foreground">Used: {formatMoney(outstanding, ccy)}</p>
                <p className="text-[11px] text-muted-foreground">Limit: {formatMoney(account.credit_limit, ccy)}</p>
              </div>
            </div>
          )}

          {/* Phase 85.1 — balance provenance + linked payment activity.
              Reported and estimated balances stay separate, and no payment
              ever silently changes the reported balance. */}
          {isRealLiability && recon && (
            <>
              <MilestoneCelebration milestone={recon.milestone} name={account.name} />
              <PaymentStreakBadge streak={recon.streak} />
              <BalanceProvenanceCard
                liabilityId={account.id}
                balance={recon.balance}
                linkedPaymentCount={activeEvents.length}
                onReviewAssumptions={onEdit}
                onUpdated={() => loadRecon()}
              />
              <PaymentActivityList
                events={recon.events || []}
                currency={ccy}
                busy={reconBusy}
                onConfirm={(e) => runReconAction(confirmPaymentEvent, e, 'Payment linked to this loan.')}
                onReject={(e) => runReconAction(rejectPaymentEvent, e, 'Marked as not this loan.')}
                onReverse={(e) => runReconAction(reversePaymentEvent, e, 'Payment unlinked.')}
              />
              {!account.closed_at && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-charcoal underline underline-offset-2 transition-colors"
                  onClick={async () => {
                    const c = await fetchCanonicalBalances();
                    setOtherLiabs(Object.entries(c?.balances || {})
                      .filter(([id]) => id !== account.id)
                      .map(([id, v]) => ({ id, name: v.name || 'Loan' })));
                    setCloseOpen(true);
                  }}
                  data-testid="liab-close-trigger"
                >
                  Mark this loan as paid off or refinanced
                </button>
              )}
              <CloseLiabilityDialog
                open={closeOpen}
                onOpenChange={setCloseOpen}
                liabilityId={account.id}
                liabilityName={account.name}
                otherLiabilities={otherLiabs}
                onClosed={() => { loadRecon(); onBack?.(); }}
              />
            </>
          )}

          {/* Phase 85.1 — credit cards intentionally do NOT use loan payment
              logic: the balance also moves with purchases, refunds, interest
              and fees, so the statement's own closing balance is the truth. */}
          {isCreditCard && (
            <div
              className="bg-card rounded-xl border border-border p-5 shadow-sm"
              data-testid="accounts-liab-card-balance-note"
            >
              <p className="text-xs font-semibold text-charcoal mb-1">How this balance is tracked</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Card balances change with purchases, refunds, interest and fees — not
                only with payments. 2120 uses the closing balance from your imported
                statements rather than subtracting payments from an older balance.
              </p>
            </div>
          )}

          {/* Upcoming payment alert */}
          {account.next_payment_date && (
            <div
              className="flex items-start gap-3 px-4 py-3.5 rounded-xl border"
              style={{ borderColor: '#D9A44160', backgroundColor: '#FBF5E8' }}
              data-testid="accounts-liab-due-alert"
            >
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: ACCOUNTS_TONE.warning }} />
              <div>
                <p className="text-sm font-medium text-charcoal">Payment due {fmtDate(account.next_payment_date)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isCreditCard
                    ? `Minimum payment: ${formatMoney(account.minimum_payment || 0, ccy)} · Full balance: ${formatMoney(outstanding, ccy)}`
                    : `EMI: ${formatMoney(account.next_payment_amount || 0, ccy)}`}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phase 84.22.6 — real Transactions tab for credit cards. Data
          source is `transactions` table filtered by account_id — the
          same source used by the /transactions page. When a CC
          statement is imported via /import, its rows land here
          automatically without any additional wiring. */}
      {tab === 'transactions' && isCreditCard && (
        <CashTransactionsTab account={account} baseCurrency={baseCurrency} />
      )}

      {tab === 'statements' && <StubTab label="Statements" />}

      {/* Phase 84.19.2 — Trends tab for credit-card accounts. Reuses the
          Cash spending pie (same shape works for CC purchases since our
          commit path types CC-payments as `transfer` and refunds as
          `refund`, so only genuine purchases hit `type='expense'`). */}
      {tab === 'trends' && isCreditCard && (
        <CashTrendsTab account={account} baseCurrency={baseCurrency} />
      )}

      {tab === 'payoff' && !isCreditCard && (
        <div className="bg-card rounded-xl border border-border p-5 lg:p-6 shadow-sm" data-testid="accounts-liab-payoff">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-4">Payoff Projection</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Outstanding balance</p>
              <p className="num font-display text-2xl font-medium" style={{ color: ACCOUNTS_TONE.liability }}>
                {formatMoney(outstanding, ccy)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Estimated payoff</p>
              <p className="font-display text-2xl font-medium text-charcoal">
                {monthsLeft ? `~${Math.floor(monthsLeft / 12)}y ${monthsLeft % 12}m` : '—'}
              </p>
            </div>
          </div>
          <div className="bg-offwhite/60 rounded-lg px-4 py-3.5">
            <p className="text-xs text-muted-foreground leading-relaxed">
              At your current EMI of{' '}
              <strong className="text-charcoal font-medium">{formatMoney(monthlyPayment, ccy)}/month</strong>,
              you would pay off this {(ACCOUNT_TYPE_LABEL[account.type] || account.type).toLowerCase()} in approximately{' '}
              <strong className="text-charcoal font-medium">
                {monthsLeft ? `${Math.floor(monthsLeft / 12)} years and ${monthsLeft % 12} months` : '—'}
              </strong>{account.interest_rate ? ` at ${account.interest_rate}% p.a.` : ''}.
            </p>
          </div>
        </div>
      )}

      {tab === 'payoff' && isCreditCard && (
        <div className="bg-card rounded-xl border border-border p-5 shadow-sm">
          <p className="text-sm font-medium text-charcoal mb-2">Pay in full each month</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Paying the full balance each month avoids interest charges. Your current balance is{' '}
            <strong className="text-charcoal">{formatMoney(outstanding, ccy)}</strong>, due{' '}
            {account.next_payment_date ? fmtDate(account.next_payment_date) : '—'}.
          </p>
        </div>
      )}
    </div>
  );
}
